'use client';

import ErrorPageRecommendations from '@/app/components/ErrorPageRecommendations';
import ErrorPageView from '@/components/ErrorPage/ErrorPageView';
import { buildRouteErrorReportOptions } from '@/components/ErrorPage/errorPage';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from '@/components/Toast';
import logger from '@/utils/client-logger';
import { reportSentryError } from '@/services/sentry';

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
  // reset: () => void;
}) {
  // TODO:这里使用logger.error会导致日志重复输出，直到内存耗尽？？

  // console.error('Error boundary caught:', error);

  const router = useRouter();
  const pathname = usePathname();
  const [recommendationsVisible, setRecommendationsVisible] = useState(true);

  /**
   * 上报客户端出现错误的情况
   */
  useEffect(() => {
    reportSentryError(
      error,
      buildRouteErrorReportOptions({
        error,
        pathname,
        online: typeof navigator === 'undefined' ? true : navigator.onLine,
      }),
    );
  }, [error, pathname]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // 监听网络状态变化
    const handleOnline = async () => {
      logger.info('[ClientPage] online');
      // 如果当前已经是联网状态（double check），再执行后续逻辑
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }

      // 1. 防抖：延迟 2 秒
      timer = setTimeout(async () => {
        try {
          // 2. 双重确认：发一个轻量请求探测（使用 favicon 或 health 接口）
          // 由于没有专门的 health 接口，这里请求根路径作为探测
          const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });

          if (res.ok) {
            // 3. 提示用户
            toast.success({
              content: 'Network recovered. Reloading...',
              duration: 2000,
            });

            // 4. 软刷新 (router.refresh)
            // 注意：router.refresh() 会重新获取服务端数据，但如果 error.tsx 是因为服务端渲染失败（如 hydration error），
            // 有时软刷新可能不够，这里根据需求使用 router.refresh() 尝试无感恢复
            router.refresh();

            // 5. 增加兜底刷新：如果软刷新后 200ms 内页面状态没有恢复（依然在 error 组件中），则执行硬刷新
            // 注意：router.refresh() 是异步的，这里使用 setTimeout 做一个简单的兜底
            setTimeout(() => {
              window.location.reload();
            }, 200);
          }
        } catch {
          // 网络虽连上但无法访问互联网，静默失败，等待下次尝试
        }
      }, 2000);
    };

    // 如果组件挂载时已经在线（可能是 SSR 错误但网络没问题），是否需要尝试重试？
    // 目前逻辑仅在状态切换时触发

    const handleOffline = () => {
      logger.info('[ClientPage] offline');
      // 如果还没来得及刷新就又断网了，取消刷新
      if (timer) {
        clearTimeout(timer);
      }
    };

    logger.info('[ClientPage] addEventListener');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      logger.info('[ClientPage] removeEventListener');
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [router]);

  const handleTryAgain = () => {
    window.location.reload();
  };

  const handleHomeNavigation = () => {
    router.push('/');
  };

  return (
    <ErrorPageView
      type="500"
      recommendationsVisible={recommendationsVisible}
      recommendations={<ErrorPageRecommendations onVisibilityChange={setRecommendationsVisible} />}
      onTryAgain={handleTryAgain}
      onBackHome={handleHomeNavigation}
    />
  );
}
